import { useEffect, useRef, useState } from 'react';
import {
  sampleDocumentHistogram,
  type HistogramChannel,
} from '../../engine/histogram';
import { getEngine } from '../../engine/engine';
import { getActiveDocument, getHistoryView, useAppStore } from '../../state/store';
import { Select } from '../components/controls';
import { Panel } from './Panel';

const CHANNEL_OPTIONS: { value: HistogramChannel; label: string }[] = [
  { value: 'rgb', label: 'RGB' },
  { value: 'r', label: 'Red' },
  { value: 'g', label: 'Green' },
  { value: 'b', label: 'Blue' },
  { value: 'luma', label: 'Luma' },
];

const BAR_COLORS: Record<HistogramChannel, string> = {
  rgb: '#c8c8c8',
  r: '#ff6666',
  g: '#66cc66',
  b: '#6699ff',
  luma: '#c8c8c8',
};

export function HistogramPanel() {
  const store = useAppStore();
  const doc = getActiveDocument(store);
  const history = getHistoryView(store, store.activeDocumentId);
  const [channel, setChannel] = useState<HistogramChannel>('rgb');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const revision =
    `${store.activeDocumentId}:${store.pixelEpoch}:${history.position}:${channel}:${doc?.activeLayerId ?? ''}`;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !doc) return;

    const engine = getEngine();
    if (!engine.available) return;

    const histogram = sampleDocumentHistogram(doc, channel);
    if (!histogram) return;

    const width = canvas.clientWidth || 256;
    const height = 80;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#3a3a3d';
    ctx.fillRect(0, 0, width, height);

    const barWidth = width / 256;
    ctx.fillStyle = BAR_COLORS[channel];
    for (let i = 0; i < 256; i += 1) {
      const value = histogram[i];
      if (value <= 0) continue;
      const barHeight = value * height;
      ctx.fillRect(i * barWidth, height - barHeight, Math.max(1, barWidth), barHeight);
    }
  }, [doc, revision, channel]);

  return (
    <Panel id="histogram" title="Histogram">
      <div className="panel__body histogram">
        {!doc && <p className="dialog__note">Open a document to view its histogram.</p>}
        {doc && (
          <>
            <Select
              value={channel}
              onChange={setChannel}
              options={CHANNEL_OPTIONS}
              title="Histogram channel"
            />
            <canvas ref={canvasRef} className="histogram__canvas" />
          </>
        )}
      </div>
    </Panel>
  );
}
